import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AuthContext, API } from "@/App";
import axios from "axios";
import { Check, Bot, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { QHubBuddyLogo, QHubFooter } from "@/components/QHubBranding";

const grades = [
  { id: "7", label: "7° Grado", description: "Séptimo grado de educación básica" },
  { id: "8", label: "8° Grado", description: "Octavo grado de educación básica" },
  { id: "9", label: "9° Grado", description: "Noveno grado de educación básica" },
  { id: "1-bach", label: "1° Año Bachillerato", description: "Primer año de bachillerato" },
  { id: "2-bach", label: "2° Año Bachillerato", description: "Segundo año de bachillerato" },
];

const SelectGrade = () => {
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [selectedGrade, setSelectedGrade] = useState(user?.grade || "");
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    if (!selectedGrade) {
      toast.error("Por favor selecciona tu grado");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(
        `${API}/auth/me`,
        { grade: selectedGrade },
        { withCredentials: true }
      );
      
      setUser({ ...user, grade: selectedGrade });
      toast.success("Grado actualizado correctamente");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Error al guardar el grado");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-mesh" data-testid="select-grade-page">
      {/* Header */}
      <header className="qhub-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-16">
            <QHubBuddyLogo size="default" />
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-lg"
        >
          <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-xl shadow-gray-200/50">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#419cca] to-[#8b5cf6] flex items-center justify-center shadow-lg shadow-[#419cca]/20">
                <GraduationCap className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold font-['Outfit'] text-gray-900 mb-2">
                Selecciona tu grado
              </h2>
              <p className="text-gray-500">
                Esto personaliza tu red de aprendizaje
              </p>
            </div>

            <div className="space-y-3 mb-8">
              {grades.map((grade, index) => (
                <motion.button
                  key={grade.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => setSelectedGrade(grade.id)}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    selectedGrade === grade.id
                      ? "border-[#419cca] bg-[#419cca]/5"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                  }`}
                  data-testid={`grade-option-${grade.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`font-semibold ${
                        selectedGrade === grade.id ? "text-[#419cca]" : "text-gray-900"
                      }`}>
                        {grade.label}
                      </p>
                      <p className="text-sm text-gray-500">{grade.description}</p>
                    </div>
                    {selectedGrade === grade.id && (
                      <div className="w-6 h-6 rounded-full bg-[#419cca] flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                </motion.button>
              ))}
            </div>

            <Button
              onClick={handleSave}
              disabled={!selectedGrade || loading}
              className="w-full h-14 rounded-xl bg-gradient-to-r from-[#419cca] to-[#8b5cf6] hover:opacity-90 text-white font-medium text-base transition-all active:scale-[0.98] shadow-lg"
              data-testid="save-grade-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                "Continuar"
              )}
            </Button>
          </div>
        </motion.div>
      </main>

      <QHubFooter />
    </div>
  );
};

export default SelectGrade;
